import React from 'react';
import { Terminal, Loader2 } from 'lucide-react';
import type { Perfil, Topico, Subtopico } from './types';

interface EcadModalsProps {
  perfis: Perfil[];
  showPerfilModal: boolean;
  setShowPerfilModal: (val: boolean) => void;
  perfilForm: { id: string; nome: string; isInterno: boolean };
  setPerfilForm: React.Dispatch<React.SetStateAction<{ id: string; nome: string; isInterno: boolean }>>;
  handleCreatePerfil: (e: React.FormEvent) => Promise<void>;

  showTopicoModal: boolean;
  setShowTopicoModal: (val: boolean) => void;
  topicoForm: { id: string; perfilId: string; titulo: string };
  setTopicoForm: React.Dispatch<React.SetStateAction<{ id: string; perfilId: string; titulo: string }>>;
  handleCreateTopico: (e: React.FormEvent) => Promise<void>;

  showSubtopicoModal: boolean;
  setShowSubtopicoModal: (val: boolean) => void;
  subtopicoForm: { 
    id: string; 
    topicoId: string; 
    titulo: string; 
    descricao: string; 
    duracao: string; 
    dificuldade: string; 
    videoUrl: string; 
    pdfUrl: string; 
  };
  setSubtopicoForm: React.Dispatch<React.SetStateAction<{ 
    id: string; 
    topicoId: string; 
    titulo: string; 
    descricao: string; 
    duracao: string; 
    dificuldade: string; 
    videoUrl: string; 
    pdfUrl: string; 
  }>>;
  handleCreateSubtopico: (e: React.FormEvent) => Promise<void>;

  showScannerModal: boolean;
  setShowScannerModal: (val: boolean) => void;
  scannerForm: { url: string; pageKey: string };
  setScannerForm: React.Dispatch<React.SetStateAction<{ url: string; pageKey: string }>>;
  scanningPage: boolean;
  handleScanPage: (e: React.FormEvent) => Promise<void>;
}

export const EcadModals: React.FC<EcadModalsProps> = ({
  perfis = [],
  showPerfilModal,
  setShowPerfilModal,
  perfilForm,
  setPerfilForm,
  handleCreatePerfil,

  showTopicoModal,
  setShowTopicoModal,
  topicoForm,
  setTopicoForm,
  handleCreateTopico,

  showSubtopicoModal,
  setShowSubtopicoModal,
  subtopicoForm,
  setSubtopicoForm,
  handleCreateSubtopico,

  showScannerModal,
  setShowScannerModal,
  scannerForm,
  setScannerForm,
  scanningPage,
  handleScanPage
}) => {
  // Check if topic slug is already in use in this profile
  const activePerfil = perfis.find(p => p.id === topicoForm.perfilId);
  const isTopicSlugInUse = activePerfil ? activePerfil.topicos.some((t: Topico) => t.id === topicoForm.id) : false;

  // Check if subtopic slug is already in use in this topic
  const activeTopico = activePerfil?.topicos.find((t: Topico) => t.id === subtopicoForm.topicoId) 
    || perfis.flatMap(p => p.topicos).find((t: Topico) => t.id === subtopicoForm.topicoId);
  const isSubtopicSlugInUse = activeTopico ? (activeTopico.subtopicos || []).some((s: Subtopico) => s.id === subtopicoForm.id) : false;

  return (
    <>
      {/* MODAL: NOVO PERFIL */}
      {showPerfilModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <form onSubmit={handleCreatePerfil} style={{
            background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px', padding: '20px', width: '360px', display: 'flex', flexDirection: 'column', gap: '14px'
          }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Novo Perfil de Acesso</h3>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>ID (slug único, ex: prefeituras)</label>
              <input 
                type="text" 
                value={perfilForm.id} 
                onChange={(e) => setPerfilForm(prev => ({ ...prev, id: e.target.value.toLowerCase().trim() }))}
                style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Nome Visível (ex: Prefeituras)</label>
              <input 
                type="text" 
                value={perfilForm.nome} 
                onChange={(e) => setPerfilForm(prev => ({ ...prev, nome: e.target.value }))}
                style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#cbd5e1', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={perfilForm.isInterno} 
                onChange={(e) => setPerfilForm(prev => ({ ...prev, isInterno: e.target.checked }))}
              />
              Perfil Interno (Somente nossa equipe)
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
              <button type="button" onClick={() => setShowPerfilModal(false)} style={{ padding: '6px 12px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" style={{ padding: '6px 12px', background: '#8b5cf6', border: 'none', color: '#fff', borderRadius: '6px', cursor: 'pointer' }}>Cadastrar</button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: NOVO TÓPICO */}
      {showTopicoModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <form onSubmit={handleCreateTopico} style={{
            background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px', padding: '20px', width: '360px', display: 'flex', flexDirection: 'column', gap: '14px'
          }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Novo Tópico Agrupador</h3>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Título do Tópico (ex: Frequência Escolar)</label>
              <input 
                type="text" 
                value={topicoForm.titulo} 
                onChange={(e) => {
                  const val = e.target.value;
                  const suggestedSlug = val
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '') // Remove accents
                    .replace(/[^a-z0-9\s_-]/g, '')   // Remove invalid chars
                    .replace(/\s+/g, '_')            // Replace spaces with underscores
                    .replace(/-+/g, '_');
                  setTopicoForm(prev => ({ ...prev, titulo: val, id: suggestedSlug }));
                }}
                style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>ID (slug único, ex: presenca)</label>
              <input 
                type="text" 
                value={topicoForm.id} 
                onChange={(e) => setTopicoForm(prev => ({ ...prev, id: e.target.value.toLowerCase().trim() }))}
                style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
              />
              {isTopicSlugInUse && (
                <span style={{ display: 'block', fontSize: '0.72rem', color: '#ef4444', fontWeight: 600, marginTop: '4px' }}>
                  ⚠️ Este ID/Slug já está em uso para este perfil!
                </span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
              <button type="button" onClick={() => setShowTopicoModal(false)} style={{ padding: '6px 12px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
              <button 
                type="submit" 
                disabled={isTopicSlugInUse || !topicoForm.id}
                style={{ 
                  padding: '6px 12px', 
                  background: (isTopicSlugInUse || !topicoForm.id) ? '#64748b' : '#8b5cf6', 
                  border: 'none', 
                  color: '#fff', 
                  borderRadius: '6px', 
                  cursor: (isTopicSlugInUse || !topicoForm.id) ? 'not-allowed' : 'pointer' 
                }}
              >
                Cadastrar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: NOVO ROTEIRO (SUBTÓPICO) */}
      {showSubtopicoModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <form onSubmit={handleCreateSubtopico} style={{
            background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px', padding: '20px', width: '420px', display: 'flex', flexDirection: 'column', gap: '14px'
          }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Novo Roteiro de Treinamento</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Título Roteiro</label>
                <input 
                  type="text" 
                  value={subtopicoForm.titulo} 
                  onChange={(e) => {
                    const val = e.target.value;
                    const suggestedSlug = val
                      .toLowerCase()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '') // Remove accents
                      .replace(/[^a-z0-9\s_-]/g, '')   // Remove invalid chars
                      .replace(/\s+/g, '_')            // Replace spaces with underscores
                      .replace(/-+/g, '_');
                    setSubtopicoForm(prev => ({ ...prev, titulo: val, id: suggestedSlug }));
                  }}
                  style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>ID (slug único, ex: roteiro_chamada)</label>
                <input 
                  type="text" 
                  value={subtopicoForm.id} 
                  onChange={(e) => setSubtopicoForm(prev => ({ ...prev, id: e.target.value.toLowerCase().trim() }))}
                  style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
                />
                {isSubtopicSlugInUse && (
                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#ef4444', fontWeight: 600, marginTop: '4px' }}>
                    ⚠️ Este ID/Slug já está em uso neste tópico!
                  </span>
                )}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Descrição</label>
              <textarea 
                value={subtopicoForm.descricao} 
                onChange={(e) => setSubtopicoForm(prev => ({ ...prev, descricao: e.target.value }))}
                rows={2}
                style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', fontFamily: 'inherit', boxSizing: 'border-box', fontWeight: 500 }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Duração</label>
                <input 
                  type="text" 
                  value={subtopicoForm.duracao} 
                  onChange={(e) => setSubtopicoForm(prev => ({ ...prev, duracao: e.target.value }))}
                  style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Dificuldade</label>
                <select 
                  value={subtopicoForm.dificuldade} 
                  onChange={(e) => setSubtopicoForm(prev => ({ ...prev, dificuldade: e.target.value }))}
                  style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
                >
                  <option value="Básico">Básico</option>
                  <option value="Médio">Médio</option>
                  <option value="Avançado">Avançado</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
              <button type="button" onClick={() => setShowSubtopicoModal(false)} style={{ padding: '6px 12px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
              <button 
                type="submit" 
                disabled={isSubtopicSlugInUse || !subtopicoForm.id}
                style={{ 
                  padding: '6px 12px', 
                  background: (isSubtopicSlugInUse || !subtopicoForm.id) ? '#64748b' : '#8b5cf6', 
                  border: 'none', 
                  color: '#fff', 
                  borderRadius: '6px', 
                  cursor: (isSubtopicSlugInUse || !subtopicoForm.id) ? 'not-allowed' : 'pointer' 
                }}
              >
                Criar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: SCANNER DE PÁGINA */}
      {showScannerModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <form onSubmit={handleScanPage} style={{
            background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px', padding: '20px', width: '420px', display: 'flex', flexDirection: 'column', gap: '14px'
          }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#34d399', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Terminal size={18} />
              Varredura de Tela (DOM Scanner)
            </h3>
            <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: 0 }}>
              Insira a URL e o identificador lógico da página para escanear botões, campos de texto e títulos no Aluno Conectado e adicioná-los automaticamente ao ui_map.json.
            </p>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>URL Alvo (ex: http://localhost:3443/login)</label>
              <input 
                type="text" 
                value={scannerForm.url} 
                onChange={(e) => setScannerForm(prev => ({ ...prev, url: e.target.value }))}
                style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Chave da Página (Page Key, ex: login_page)</label>
              <input 
                type="text" 
                value={scannerForm.pageKey} 
                onChange={(e) => setScannerForm(prev => ({ ...prev, pageKey: e.target.value.toLowerCase().trim() }))}
                style={{ width: '100%', padding: '8px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#000000', boxSizing: 'border-box', fontWeight: 500 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
              <button type="button" onClick={() => setShowScannerModal(false)} disabled={scanningPage} style={{ padding: '6px 12px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" disabled={scanningPage} style={{ padding: '6px 12px', background: '#10b981', border: 'none', color: '#fff', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {scanningPage ? <Loader2 size={12} className="spin" /> : null}
                Iniciar Varredura
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
